import { applyDagreLayout } from './flowchartUtils';

/**
 * Transforms hierarchical competency data into React Flow nodes and edges
 * for the detailed flowchart view (no action deduplication, includes entry nodes)
 */
export function transformToDetailedFlowData(values, direction = 'LR') {
  const nodes = [];
  const edges = [];

  values.forEach((value, vIdx) => {
    const valueId = `value-${vIdx}`;
    const totalActions = value.competencies?.reduce((sum, c) => sum + (c.actions?.length || 0), 0) || 0;

    // Value node
    nodes.push({
      id: valueId,
      type: 'valueNode',
      data: {
        name: value.valueName,
        competencyCount: value.competencies?.length || 0,
        actionCount: totalActions,
      },
      position: { x: 0, y: 0 },
    });

    value.competencies?.forEach((comp, cIdx) => {
      const competencyId = `competency-${vIdx}-${cIdx}`;

      // Competency node
      nodes.push({
        id: competencyId,
        type: 'competencyNode',
        data: {
          name: comp.competencyName,
          actionCount: comp.actions?.length || 0,
          valueId: valueId,
        },
        position: { x: 0, y: 0 },
      });

      // Edge: Value -> Competency
      edges.push({
        id: `e-${valueId}-${competencyId}`,
        source: valueId,
        target: competencyId,
        type: 'smoothstep',
      });

      // Actions for this competency (NO deduplication)
      comp.actions?.forEach((action, aIdx) => {
        const actionId = `action-${vIdx}-${cIdx}-${aIdx}`;
        const uniqueSkills = new Set((action.entries || []).filter(e => e.skill).map(e => e.skill));
        const uniqueRoles = new Set((action.entries || []).flatMap(e => e.roles || []));

        // Action node
        nodes.push({
          id: actionId,
          type: 'actionNode',
          data: {
            name: action.actionName,
            skillCount: uniqueSkills.size,
            roleCount: uniqueRoles.size,
            entryCount: action.entries?.length || 0,
            entries: action.entries || [],
            competencyIds: [competencyId],
            competencyNames: [comp.competencyName],
            isMultiParent: false,
            multiActionCompetencyIds: [],
          },
          position: { x: 0, y: 0 },
        });

        // Edge: Competency -> Action
        edges.push({
          id: `e-${competencyId}-${actionId}`,
          source: competencyId,
          target: actionId,
          type: 'smoothstep',
        });

        // Entry nodes for this action
        action.entries?.forEach((entry, eIdx) => {
          const entryId = `entry-${vIdx}-${cIdx}-${aIdx}-${eIdx}`;

          nodes.push({
            id: entryId,
            type: 'entryNode',
            data: {
              skill: entry.skill,
              attitude: entry.attitude,
              roles: entry.roles || [],
              learning: entry.learning,
            },
            position: { x: 0, y: 0 },
          });

          // Edge: Action -> Entry
          edges.push({
            id: `e-${actionId}-${entryId}`,
            source: actionId,
            target: entryId,
            type: 'smoothstep',
          });
        });
      });
    });
  });

  // Use shared layout function with 'detailed' preset
  const layoutedNodes = applyDagreLayout(nodes, edges, direction, { preset: 'detailed' });

  return { nodes: layoutedNodes, edges };
}
